import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Signs an avatar storage path on demand and returns a fresh URL.
 *
 * `profiles.avatar_url` stores the storage PATH (e.g. `{user_id}/avatar-123.webp`),
 * NOT a full signed URL. This hook mints a short-lived signed URL at render time
 * so avatars never break when tokens rotate or expiry windows lapse.
 *
 * - TTL: 1 hour
 * - Cached in-memory across mounts by path
 * - Auto-refreshes when the cached URL is within 5 minutes of expiry
 */

const TTL_SECONDS = 60 * 60; // 1 hour
const REFRESH_WINDOW_MS = 5 * 60 * 1000; // 5 minutes before expiry

type CacheEntry = { url: string; expiresAt: number };
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string | null>>();

async function signPath(path: string): Promise<string | null> {
  // Legacy tolerance: some older `profiles.avatar_url` rows stored a full
  // http(s) URL instead of a storage path. Pass those through unchanged so
  // the Avatar renders instead of falling back to initials on stale data.
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const existing = inflight.get(path);
  if (existing) return existing;
  const p = (async () => {
    const { data, error } = await supabase.storage
      .from("avatars")
      .createSignedUrl(path, TTL_SECONDS);
    if (error || !data?.signedUrl) return null;
    cache.set(path, {
      url: data.signedUrl,
      expiresAt: Date.now() + TTL_SECONDS * 1000,
    });
    return data.signedUrl;
  })().finally(() => inflight.delete(path));
  inflight.set(path, p);
  return p;
}


export function useAvatarUrl(path?: string | null): { url: string | null; loading: boolean } {
  const [url, setUrl] = useState<string | null>(() => {
    if (!path) return null;
    const hit = cache.get(path);
    if (hit && hit.expiresAt - Date.now() > REFRESH_WINDOW_MS) return hit.url;
    return null;
  });
  const [loading, setLoading] = useState<boolean>(!!path && !url);

  useEffect(() => {
    let cancelled = false;
    if (!path) {
      setUrl(null);
      setLoading(false);
      return;
    }
    const hit = cache.get(path);
    if (hit && hit.expiresAt - Date.now() > REFRESH_WINDOW_MS) {
      setUrl(hit.url);
      setLoading(false);
      return;
    }
    setLoading(true);
    signPath(path).then((next) => {
      if (cancelled) return;
      setUrl(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return { url, loading };
}

export default useAvatarUrl;
